import { useContext, useState } from 'react';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { StepWizardChildProps } from 'react-step-wizard';
import { toast } from 'react-toastify';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import { CustomToastContent } from '../components/CustomToastContent';
import { BranchesContext } from '../context/BranchesContext';
import { RequestContext } from '../context/RequestContext';
import { Task, TaskBranchInfo, TaskDescription } from '../models/dashboard-models';
import { HandoverItem, HandoverProps, RentalAgreementProps } from '../models/warehouse-models';
import { constructHandoverItems } from '../utils/custom-hooks';
import { fetchMapBranches, fetchTasksAll } from '../utils/dashboard-hooks';
import { toastOptionsTopRight } from '../utils/Enums/ToastOptions';
import { transformDateToString } from '../utils/helper-functions';
import {
    checkForItemsInInventory,
    getHandover,
    getRentalAgreement,
    useGetAllShortPassports,
    useGetHandoverProtocols,
    useGetUserInventory,
} from '../utils/warehouses-hooks';
import { addTaskFromRequest, useGetAllTaskSkeletons } from '../utils/work-hooks';
import dateFormat from 'dateformat';
import { getBranchPaymentInfo } from '../utils/clients-hooks';

export const Documents: React.FC<Partial<StepWizardChildProps>> = ({ previousStep }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { t: errorsTranslation } = useTranslation('translation', { keyPrefix: 'errors' });
    const { t: newRequestTranslation } = useTranslation('translation', { keyPrefix: 'newRequest' });
    const { taskDescription, tasksBranchesInfo, setTaskDescription, setTasksBranchesInfo, setShowModal } =
        useContext(RequestContext);
    const { targetDate, assigneeId, category } = taskDescription;
    const { getNewBranches, setLastSelectedBranch } = useContext(BranchesContext);

    const [loading, setLoading] = useState(false);

    const [getPassports] = useAsyncResource(useGetAllShortPassports, false);
    const passports = getPassports();

    const constructReturnedDeviceHandoverItems = (task: Task): HandoverItem[] => {
        return (task.devices ?? []).map((device) => ({
            itemId: device.passportId,
            itemName: device.deviceName || device.model,
            itemCount: 1,
            itemType: 'DEVICE',
            cost: 0,
            oilName: '',
            oilQuantity: 0,
        }));
    };

    const print = async (branch: TaskBranchInfo, task: Task) => {
        const branchTargetDate = branch.targetDate || targetDate || '';
        const handoverData: HandoverProps = {
            representative: '',
            date: transformDateToString(branchTargetDate, 'dd.mm.yyyy'),
            town: branch.city,
            taskItemsVO: {
                branchId: branch.branchId,
                userId: +assigneeId,
                neededItems:
                    category === 'DEVICE_RETURN'
                        ? constructReturnedDeviceHandoverItems(task)
                        : constructHandoverItems(branch.items, passports),
                taskId: task.id,
                taskCategory: category,
            },
            save: false,
        };
        const pdfFile = await getHandover(handoverData);
        if (pdfFile) {
            const pdf = URL.createObjectURL(pdfFile);
            window.open(pdf, '_blank');
            return true;
        } else {
            toast.error(<CustomToastContent content={[`${t('printError')}`]} whiteText />, toastOptionsTopRight);
            return false;
        }
    };
    const printRentalAgreement = async (branch: TaskBranchInfo, models: string[]): Promise<boolean> => {
        //todo: If no devices present, dont print
        const paymentInfo = await getBranchPaymentInfo(branch.branchId);
        const rentalAgreementProps: RentalAgreementProps = {
            branchId: branch.branchId,
            reason: branch.reason ?? '',
            inCash: paymentInfo.paymentMethod === 'CASH',
            models: models,
        };
        if (rentalAgreementProps.models?.length === 0) {
            return false;
        } else {
            const pdfFile = await getRentalAgreement(rentalAgreementProps);
            if (pdfFile) {
                const pdf = URL.createObjectURL(pdfFile);
                window.open(pdf, '_blank');
                return true;
            } else {
                return false;
            }
        }
    };

    const createTask = async (branch: TaskBranchInfo) => {
        const branchTargetDate = branch.targetDate || targetDate || '';
        const models = passports
            .filter((value: any) => value.type === 'DEVICE' && branch.items.find((item) => value.id === item.itemId))
            .map((passport) => passport.model);
        //TODO: investigate creating multiple task error from request.
        if (!(branch.rentalAgreement && models.length === 0)) {
            let res = await addTaskFromRequest(
                branch.branchId,
                branchTargetDate,
                category,
                branch.items,
                +assigneeId,
                branch.details
            );
            if (branch.handover && res) {
                await print(branch, res);
            }
            if (branch.rentalAgreement && res) {
                await printRentalAgreement(branch, models);
            }
            return res;
        } else {
            toast.error(
                <CustomToastContent content={[`${errorsTranslation('noItemsToPrintRental')}`]} whiteText />,
                toastOptionsTopRight
            );
        }
    };

    const onSubmitClick = async () => {
        setLoading(true);
        if (!assigneeId || !category) {
            setLoading(false);
            return toast.error(<CustomToastContent content={[`${t('selectFields')}`]} whiteText />);
        }
        const getResponses = async () => {
            let responses = [];
            for (const branch of tasksBranchesInfo) {
                let response = await createTask(branch);
                responses.push(response);
            }
            return responses;
        };

        const responses = await getResponses();

        setLoading(false);

        if (responses.includes(undefined)) {
            toast.warning(
                <CustomToastContent content={[`${t('notAllTasksCreated')}`]} whiteText />,
                toastOptionsTopRight
            );
        } else {
            toast.success(<CustomToastContent content={[`${t('taskCreated')}`]} whiteText />, toastOptionsTopRight);
            setTaskDescription({
                targetDate: dateFormat(new Date(), 'yyyy-mm-dd'),
            } as TaskDescription);

            // REMOVED: resourceCache(fetchMapBranches).clear();
            // REMOVED: await getNewBranches({ filter: undefined });
            // Task creation doesn't modify branch data, no need to refetch branches
            resourceCache(fetchTasksAll).clear();
            resourceCache(useGetAllTaskSkeletons).clear();
            resourceCache(useGetHandoverProtocols).clear();
            resourceCache(useGetUserInventory).clear();
            resourceCache(checkForItemsInInventory).clear();
            setShowModal(false);
            setLastSelectedBranch(null);
        }
    };

    return (
        <div className='wizard-content d-flex flex-column justify-content-between'>
            <div className='w-100 px-2 total-materials'>
                <h5 className='pb-2'>{t('branches')}</h5>
                <Table
                    striped
                    hover
                    responsive
                    className='mt-2 border border-dark'
                    style={{
                        tableLayout: 'fixed',
                    }}
                >
                    <thead>
                        <tr>
                            <th className='fw-bold w-25 text-center'>{t('address')}</th>
                            <th className='fw-bold w-25 text-center'>{t('company')}</th>
                            <th className='fw-bold w-25 text-center'>{t('branches')}</th>
                            <th className='fw-bold w-25 text-center'>{t('executionDate')}</th>
                            <th className='fw-bold w-25  text-center'>{t('handover')}</th>
                            <th className='fw-bold w-25  text-center'>{t('rentalAgreement')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tasksBranchesInfo.map((branch, index) => (
                            <tr key={index}>
                                <td className='text-center'>{branch.branchAddress}</td>
                                <td className='text-center'>{branch.companyName}</td>
                                <td className='text-center'>{branch.branchName}</td>
                                <td>
                                    <Form.Control
                                        type='date'
                                        value={branch.targetDate || targetDate}
                                        onChange={(e) => {
                                            let items = [...tasksBranchesInfo];
                                            items[index].targetDate = e.target.value;
                                            setTasksBranchesInfo(items);
                                        }}
                                        className='form-control'
                                    />
                                </td>

                                <td className='text-center'>
                                    <Form.Check
                                        readOnly
                                        checked={branch.handover}
                                        type='checkbox'
                                        disabled
                                        onClick={() => {
                                            let items = [...tasksBranchesInfo];
                                            items[index].handover = !items[index].handover;
                                            setTasksBranchesInfo(items);
                                        }}
                                    />
                                </td>
                                <td className='d-flex align-items-center'>
                                    <Form.Check
                                        className='mx-2'
                                        readOnly
                                        checked={branch.rentalAgreement}
                                        type='checkbox'
                                        onClick={() => {
                                            let items = [...tasksBranchesInfo];
                                            items[index].rentalAgreement = !items[index].rentalAgreement;
                                            setTasksBranchesInfo(items);
                                        }}
                                    />
                                    <textarea
                                        placeholder={newRequestTranslation('reason')}
                                        onChange={(e) => {
                                            tasksBranchesInfo[index].reason = e.target.value;
                                        }}
                                        disabled={!tasksBranchesInfo[index].rentalAgreement}
                                        inputMode='text'
                                        className='w-100px textarea form-control'
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
            <div className='d-flex wizard-buttons w-100 justify-content-between'>
                <Button onClick={() => previousStep!()}>{t('previous')}</Button>
                <div className='w-25 d-flex justify-content-end'>
                    <Button onClick={onSubmitClick}>
                        {loading ? <Spinner animation='border' size='sm' /> : t('printAndSave')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
